import {
    selectSearchText,
    setSearchText,
} from 'components/customer/Product/store/productSlice';
import {useProductContext} from 'contexts/ProductContext';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Form, InputGroup} from 'react-bootstrap';
import {useSearchParams} from 'react-router-dom';
import Icon from 'shared/Icon';
import {useAppDispatch, useAppSelector} from 'store/customer';
import styled, {keyframes, css} from 'styled-components';
import {useDebouncedCallback} from 'use-debounce';
import {mobile, smallDevices} from 'shared/Breakpoints';

interface SearchProps {
    isProductPage?: boolean;
    placeholder?: string;
    compact?: boolean;
}

// this is kind of auto search. if rails is true in search query we search "rail" by default when this page is loaded.
export const Search = ({
    isProductPage = true,
    placeholder,
    compact = false,
}: SearchProps) => {
    const dispatch = useAppDispatch();

    const {breadcrumbs} = useProductContext();
    const search = useAppSelector(selectSearchText);

    const [animate, setAnimate] = useState(false);
    const [searchParams] = useSearchParams();
    const [localSearch, setLocalSearch] = useState(search ?? '');

    const startAnimation = useCallback((stopDelay?: number) => {
        setAnimate(true);

        if (stopDelay) {
            setTimeout(() => {
                setAnimate(false);
            }, stopDelay);
        }
    }, []);

    // Keep the input feeling instant while typing, but wait for a pause
    // before pushing the value to global state so downstream consumers
    // (e.g. category/item toggling) don't flicker on every keystroke.
    const debouncedSetSearchText = useDebouncedCallback((value: string) => {
        dispatch(setSearchText(value));
    }, 500);

    const changeHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const {value} = event.target;
            setLocalSearch(value);
            debouncedSetSearchText(value);
        },
        [debouncedSetSearchText]
    );

    const clearSearch = useCallback(() => {
        setLocalSearch('');
        dispatch(setSearchText(''));
    }, []);

    useEffect(() => {
        setLocalSearch(search ?? '');
    }, [search]);

    useEffect(() => {
        startAnimation(5000);
        return () => {
            dispatch(setSearchText(''));
        };
    }, []);

    useEffect(() => {
        if (searchParams.has('rails')) {
            dispatch(setSearchText('rail'));
        }
    }, [searchParams]);

    const keyRef = useRef('all products');
    const key = useMemo(() => {
        const hasCategory = searchParams.has('category');
        const hasSubCategory = searchParams.has('subCategory');

        let key = 'all products';

        if (hasCategory || hasSubCategory) {
            if (breadcrumbs && breadcrumbs.length > 0) {
                const categoryName = breadcrumbs[breadcrumbs.length - 1];

                if (categoryName) {
                    key = categoryName.key;
                }
            }

            if (
                key == 'all products' &&
                breadcrumbs &&
                breadcrumbs.length == 0
            ) {
                return keyRef.current;
            }

            keyRef.current = key;
        }
        return key;
    }, [breadcrumbs, search, searchParams]);

    const inputRef = useRef<HTMLInputElement>(null);
    const searchRef = useRef<string>();

    useEffect(() => {
        // search is not usually cleared to be
        // undefined without manually calling
        // redux dispatcher. so we use it to
        // detect manual search clear and auto
        // focus the input
        if (search == undefined) {
            if (searchRef.current != '') {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
        }
    }, [search]);

    return (
        <ShimmerWrapper $animate={animate} $compact={compact}>
            <Container className="searchKeysNInput" $compact={compact}>
                <Title $compact={compact}>
                    Search {key == 'all products' ? '' : 'within'} {key}{' '}
                    {key == 'all products' ? '' : 'category'}
                </Title>
                <LocalInputGroup $compact={compact}>
                    <InputGroup.Text as="label" htmlFor="search">
                        <Icon
                            iconName="search-grey.svg"
                            width="20px"
                            style={{width: 20}}
                        />
                    </InputGroup.Text>
                    <Form.Control
                        ref={inputRef}
                        autoFocus
                        id="search"
                        placeholder={
                            placeholder ??
                            (isProductPage
                                ? 'Try searching "drawer", "panel", or "corner"...'
                                : 'Type to search...')
                        }
                        as="input"
                        className="searchField"
                        value={localSearch}
                        onChange={changeHandler}
                        autoComplete="off"
                    />
                    <InputGroup.Text
                        as="label"
                        htmlFor="search"
                        onClick={clearSearch}>
                        <Icon
                            iconName="Button-Cancel-Transparent.svg"
                            width="20px"
                            style={{width: 20}}
                        />
                    </InputGroup.Text>
                </LocalInputGroup>
            </Container>
        </ShimmerWrapper>
    );
};

const borderShimmer = keyframes`
    0% { background-position: 200% center; }
    100% { background-position: -200% center; }
`;

const ShimmerWrapper = styled.div<{$animate: boolean; $compact?: boolean}>`
    position: relative;
    border-radius: 10px;
    margin-top: ${({$compact}) => ($compact ? '40px' : '15px')};

    ${({$animate, theme}) => {
        if ($animate) {
            return css`
                padding: 2px;
                background: linear-gradient(
                    90deg,
                    #eb0000 0%,
                    #95008a 25%,
                    #3300fc 50%,
                    #95008a 75%,
                    #eb0000 100%
                );
                background-size: 200% auto;
                animation: ${borderShimmer} 3s linear infinite;
            `;
        } else {
            return `
                border: 2px solid ${theme.colors.primary?.main};
            `;
        }
    }}
`;

const Container = styled.div<{$compact?: boolean}>`
    border-radius: 10px;
    padding: ${({$compact}) => ($compact ? '8px' : '15px')};
    background: #f5f5f5;

    ${({$compact}) =>
        $compact
            ? css`
                  ${smallDevices`
                      padding: 6px;
                  `}

                  ${mobile`
                      padding: 8px 12px;
                  `}
              `
            : ''}
`;

const Title = styled.h1<{$compact?: boolean}>`
    color: ${({theme}) => theme.colors.primary?.main};
    background: #f5f5f5 !important;
    padding: 0 !important;
    border-radius: 0 !important;
    margin: ${({$compact}) => ($compact ? '0 0 4px 0' : '0 0 10px 0')} !important;
    font-size: ${({$compact}) => ($compact ? '0.85em' : '1.05em')};
    font-weight: 500;
`;

const LocalInputGroup = styled(InputGroup)<{$compact?: boolean}>`
    border: 1px solid #b3b3b3;
    border-radius: 7px;
    padding: ${({$compact}) => ($compact ? '2px' : '5px')};
    background: white;

    ${({$compact}) =>
        $compact
            ? css`
                  ${smallDevices`
                      padding: 1px 2px;
                  `}

                  ${mobile`
                      padding: 2px 4px;
                  `}
              `
            : ''}

    > label {
        background: white;
        cursor: pointer;
    }

    > input {
        padding-left: 0;

        &::placeholder {
            color: #878787;
        }
    }

    > label,
    > input {
        border: 0;
        box-shadow: none !important;
    }
`;
